import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '../../ui';

type Props = {
  children: ReactNode;
  /** What failed, in the user's terms — "This chart", "The dashboard". */
  subject?: string;
  /** Rendered instead of the default panel. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
};

type State = { error: Error | null };

/**
 * The last line of defence.
 *
 * A React render error used to take the whole product to a bare panel showing the
 * raw exception message. Two things changed: the message is no longer the
 * headline (a stack fragment tells a user nothing), and the boundary can be
 * scoped, so one broken panel does not blank the surfaces around it.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
     
    console.error('Analytics crashed:', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    const subject = this.props.subject ?? 'This part of the app';
    return (
      <div className="grid min-h-full flex-1 place-items-center p-6">
        <div className="w-full max-w-md rounded-xl border border-border bg-surface p-6 text-center shadow-sm">
          <span className="mx-auto mb-3 flex size-10 items-center justify-center rounded-lg bg-danger-bg text-danger">
            <AlertTriangle className="size-5" />
          </span>
          <h2 className="font-display text-sm font-semibold text-fg">{subject} stopped working</h2>
          <p className="mt-1.5 text-xs leading-relaxed text-fg-muted">
            Nothing you saved was affected. Try again — if it keeps happening, reload the page and let us
            know what you were doing.
          </p>
          <details className="mt-3 text-left">
            <summary className="cursor-pointer text-xs text-fg-faint">Technical detail</summary>
            <pre className="mt-2 max-h-32 overflow-auto rounded-md bg-surface-hover p-2 font-mono text-[0.6875rem] text-fg-muted">
              {error.message}
            </pre>
          </details>
          <div className="mt-4 flex justify-center gap-2">
            <Button variant="secondary" icon={<RotateCcw />} onClick={this.reset}>
              Try again
            </Button>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
